import { Box, CircularProgress, FormControlLabel, Link, Radio, RadioGroup, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchEcomPackages } from '../state/actions'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReducerState } from 'state/reducers'
interface EcomPackage {
  ModuleID: string
  ModuleName: string
  Price: number
  App_Id: number
  ou_name: string
}
export interface EcomPackagesResponse {
  Data: EcomPackage[]
}

interface EcomPackProps {
  customer: {
    CustEosId: string
    CustNo: string
    CustName: string
    Location: string
    Street: string
    ZipCode: string
    OuName: string
    ShipToLoc: string
  }
  setSelectedPackage: Dispatch<SetStateAction<null | { ModuleID: string; ModuleName: string; Price: number }>>
  setAppId: Dispatch<SetStateAction<number | null>>
  selectedPackage: null | { ModuleID: string; ModuleName: string; Price: number }
  allowProceed: (allow: boolean) => void
}
const StyledRadioGroup = styled(RadioGroup)(() => ({
  marginLeft: '20px'
}))

const InfoText = styled(Typography)(() => ({
  marginTop: '10px'
}))

const EcomPack: React.FC<EcomPackProps> = props => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const customerNumber = props.customer.CustEosId
  const [ecomPackages, setEcomPackages] = useState<EcomPackage[] | null>(null)
  const [ecomSelectedPackage, setEcomSelectedPackage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const App_Id = useSelector((state: IRootReducerState) => state.auth?.user?.AppId)
  const ou_name = useSelector((state: IRootReducerState) => state.auth?.user?.ou_name)
  useEffect(() => {
    let isMounted = true
    const getEcomPackage = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = (await dispatch(fetchEcomPackages(customerNumber, App_Id || '', ou_name || ''))) as unknown as EcomPackagesResponse
        if (isMounted) {
          if (response?.Data && response.Data.length > 0) {
            setEcomPackages(response.Data)
            if (props.selectedPackage) {
              setEcomSelectedPackage(props.selectedPackage.ModuleID)
              props.allowProceed(true)
            } else {
              props.allowProceed(false)
            }
          } else {
            setEcomPackages([])
            setError(t('Customers.NoPackagesAvailable'))
            props.allowProceed(false)
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching e-commerce packages:', error)
          setError(t('Customers.ErrorFetchingPackages'))
          props.allowProceed(false)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    if (customerNumber) {
      getEcomPackage()
    }
    return () => {
      isMounted = false
    }
  }, [dispatch, customerNumber, t])

  const handlePackageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value
    setEcomSelectedPackage(selectedValue)

    if (!loading && !error) {
      const selectedEcomPack = ecomPackages?.find(packageItem => packageItem.ModuleID === selectedValue)
      if (selectedEcomPack) {
        props.setSelectedPackage({
          ModuleID: selectedEcomPack.ModuleID,
          ModuleName: selectedEcomPack.ModuleName,
          Price: selectedEcomPack.Price
        })
        props.setAppId(selectedEcomPack.App_Id)
      }

      props.allowProceed(true)
    }
  }

  useEffect(() => {
    props.allowProceed(!loading && !error && ecomSelectedPackage !== '')
  }, [loading, error, ecomSelectedPackage, props])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <CircularProgress size={20} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Typography variant='body1' color='error'>
          {error}
        </Typography>
      </Box>
    )
  }

  if (!ecomPackages || ecomPackages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Typography variant='body1' color='textSecondary'>
          {t('Customers.NoPackagesAvailable')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' style={{ marginBottom: '22px', marginTop: '0px', fontSize: 20, fontWeight: 700 }}>
        {t('Customers.EcommercePackage')}
      </Typography>
      <Typography gutterBottom>{t('Customers.SelectPackagePrompt')}</Typography>
      <StyledRadioGroup role='radiogroup' data-testid='ecom-package' value={ecomSelectedPackage} onChange={handlePackageChange}>
        {ecomPackages.map((packageItem: EcomPackage) => (
          <FormControlLabel
            key={packageItem.ModuleID}
            value={packageItem.ModuleID}
            control={<Radio />}
            label={`${packageItem.ModuleName} | €${packageItem.Price.toFixed(2)}`}
          />
        ))}
      </StyledRadioGroup>
      <InfoText variant='body2'>
        {t('Customers.PVKompassInfo')}{' '}
        <Link href='https://www.pvautomotive.de/pvkompass/' target='_blank' underline='hover'>
          {t('Customers.here')}
        </Link>
        .
      </InfoText>
    </Box>
  )
}

export default EcomPack
